import { useCallback, useEffect, useState } from 'react';
import { applyReuse, findReuseSources } from '../../../../services/songReuseService';

/**
 * "Another student already has this song set up" for a song just added
 * (2026-11-01; songReuseModel.js says what is offered and copied).
 *
 * Looks once per set-up song. `status` is 'idle' (no song being set up),
 * 'checking', 'ready' (sources found) or 'none' (nothing to offer, or the
 * lookup failed: a failed lookup must never block setting the song up the
 * ordinary way, so it falls through to the lyric-video search and is logged).
 *
 * apply() writes, then patches the board song in place and reloads the
 * student's tracks, so the words, videos and pills appear without a page load.
 */
const useSongReuse = ({ song, studentId, studentName, setSongs, reloadTracks }) => {
	const songId = song ? song.id : null;
	const canonicalSongId = song ? song.canonicalSongId || null : null;
	const [state, setState] = useState({ forId: null, status: 'idle', sources: [] });

	useEffect(() => {
		if (!songId || !studentId) {
			setState({ forId: null, status: 'idle', sources: [] });
			return undefined;
		}
		if (!canonicalSongId) {
			setState({ forId: songId, status: 'none', sources: [] });
			return undefined;
		}
		let live = true;
		setState({ forId: songId, status: 'checking', sources: [] });
		findReuseSources({ canonicalSongId, studentId })
			.then((sources) => {
				if (live) setState({ forId: songId, status: sources.length ? 'ready' : 'none', sources });
			})
			.catch((e) => {
				console.error('Looking for another student’s setup of this song:', e);
				if (live) setState({ forId: songId, status: 'none', sources: [] });
			});
		return () => {
			live = false;
		};
	}, [songId, canonicalSongId, studentId]);

	const apply = useCallback(
		async (source) => {
			const res = await applyReuse({ source, song, studentId, studentName });
			const p = res.patch || {};
			setSongs((prev) =>
				prev.map((s) => {
					if (s.id !== song.id) return s;
					const next = { ...s };
					if (p.lyrics) next.lyrics = p.lyrics;
					if (p.sections) next.sections = p.sections;
					if (p.sectionsMeta) next.sectionsMeta = p.sectionsMeta;
					const videos = { ...(s.videos || {}) };
					const marks = { ...(s.sectionMarks || {}) };
					for (const [k, v] of Object.entries(p)) {
						if (k.startsWith('videos.')) videos[k.slice(7)] = v;
						if (k.startsWith('sectionMarks.')) marks[k.slice(13)] = v;
					}
					next.videos = videos;
					next.sectionMarks = marks;
					return next;
				})
			);
			if (res.tracksAdded && reloadTracks) reloadTracks();
			return res;
		},
		[song, studentId, studentName, setSongs, reloadTracks]
	);

	// Between a new song arriving and the effect above running, the state still
	// describes the previous song: read that window as 'checking', never as
	// the old song's sources.
	const current = !songId || !studentId ? { status: 'idle', sources: [] } : state.forId === songId ? state : { status: 'checking', sources: [] };
	return { status: current.status, sources: current.sources, apply };
};

export default useSongReuse;
